import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import PurposeStats from '../../components/common/PurposeStats';
import { apiErrorText, apiRequest, saveWithOptionalFile, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import { LockedCreateButton, useCanBuild } from '../../components/common/ModuleInterest';

/* The themes a company runs its year in — "Wellbeing", "Our first quarter".

   Written HERE and not on the journey, which is where they used to be editable while this screen
   only read them: the themes and the programmes are the two ways a company groups its year, so
   they belong beside each other. The journey links across.

   ★ NOT tied to a programme. Nothing in the data associates the two, so a theme shown "under" a
   programme would be the same list repeated identically under every one. They are siblings, and
   an event says which of both it serves through its own purpose picker.

   ⚠️ Most companies have no themes at all — one journey blob exists platform-wide. The empty
   state is the state this screen is usually in, so it has to read as an invitation rather than
   as something having gone wrong.

   ★★ **Windows may overlap, and a running theme is edited like any other** (2026-09-13). Until
   then two themes could not cover the same day: the save was refused, by name, on both sides of
   the wire. What that rule protected was the badge on the two homes, which had room for exactly
   ONE theme name — not the calendar, where "Wellbeing all quarter, Recognition for the fortnight
   inside it" is simply how a year is planned. The badge stopped naming a theme instead. Several
   rows here can wear the "Running" pill at once, and that is the honest answer. */

const EMPTY_THEME = { id: '', name: '', start: '', end: '', description: '', link: '' };

const CompanyThemes = () => {
  const { t, i18n } = useTranslation();
  // The paid "Own events" module behind every build button here (null while it loads).
  const canBuild = useCanBuild();
  const navigate = useNavigate();
  const [themes, setThemes] = useState(null);
  const [draft, setDraft] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/journey/themes')
      .then((res) => setThemes(res.themes || []))
      // Not a contact person, or not a Bravos BU — either way this screen is not for you.
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    if (busy || !draft || !draft.name.trim()) return;
    setBusy(true);
    setNotice(null);
    saveWithOptionalFile('/bravos/journey/theme', draft, file)
      .then((res) => {
        setThemes(res.themes || []);
        setDraft(null);
        setFile(null);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const remove = (theme) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteThemeConfirm', { name: theme.name }))) return;
    setBusy(true);
    apiRequest('DELETE', `/bravos/journey/theme/${theme.id}`)
      .then((res) => setThemes(res.themes || []))
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const rows = themes || [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="calendar-lines" theme="primary" className="me-2" />
          {t('journeyThemes')}
        </Title>

        {themes === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('journeyThemesNote')}</p>
            {rows.length === 0 && !draft && <p className="ce-note">{t('noThemesYet')}</p>}

            {rows.map((th) => {
              const live = (!th.start || th.start <= today) && (!th.end || th.end >= today);
              return (
                <div key={th.id} className="bravos-themerow">
                  <span
                    className="bravos-themerow__img"
                    style={th.image ? { backgroundImage: `url("${th.image}")` } : undefined}
                  />
                  <span className="bravos-themerow__body">
                    <span className="bravos-themerow__name">{th.name}</span>
                    <span className="bravos-themerow__when">
                      {[shortDate(th.start, i18n.language), shortDate(th.end, i18n.language)]
                        .filter(Boolean).join(' – ') || t('themeNoWindow')}
                    </span>
                  </span>
                  {live && <span className="bravos-themerow__live">{t('themeLive')}</span>}
                  {/* Including a running one: its dates, its picture and its words are exactly
                      what an admin comes back to change once the theme is out there. */}
                  <button
                    type="button"
                    className="bravos-member__makeadmin"
                    onClick={() => { setDraft({ ...EMPTY_THEME, ...th }); setFile(null); }}
                  >
                    {t('edit')}
                  </button>
                  <button
                    type="button"
                    className="bravos-member__remove"
                    onClick={() => remove(th)}
                    disabled={busy}
                    title={t('delete')}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              );
            })}

            {draft ? (
              <div className="bravos-form">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={120}
                  placeholder={t('themeNamePlaceholder')}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <div className="bravos-form__row">
                  <label className="bravos-form__label">
                    {t('from')}
                    <input
                      type="date"
                      value={draft.start || ''}
                      onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                    />
                  </label>
                  <label className="bravos-form__label">
                    {t('to')}
                    <input
                      type="date"
                      value={draft.end || ''}
                      onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                    />
                  </label>
                </div>
                <textarea
                  rows={3}
                  maxLength={600}
                  value={draft.description || ''}
                  placeholder={t('themeDescPlaceholder')}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
                <input
                  type="url"
                  value={draft.link || ''}
                  placeholder={t('themeLinkPlaceholder')}
                  onChange={(e) => setDraft({ ...draft, link: e.target.value })}
                />
                <label className="bravos-form__label">
                  {t('themeImage')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                  />
                </label>
                <div className="bravos-form__row">
                  <button
                    type="button"
                    className="bravos-submit"
                    onClick={save}
                    disabled={busy || !draft.name.trim()}
                  >
                    {busy ? t('pleaseWait') : t('save')}
                  </button>
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => { setDraft(null); setFile(null); }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (canBuild === false ? (
              /* Seeing the themes is free, making them is the paid "Own events" module. */
              <LockedCreateButton label={t('addTheme')} />
            ) : (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => { setDraft({ ...EMPTY_THEME }); setFile(null); }}
              >
                <Icon name="plus" /> {t('addTheme')}
              </button>
            ))}
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      {/* What the themes came to, under what they ARE — the order matters: an admin arrives here
          to write a theme down, and reads how the last one went on the way past. */}
      <Card>
        <Title my="1">{t('purposeStatsTitle')}</Title>
        <PurposeStats kind="theme" />
      </Card>
    </Base>
  );
};

export default CompanyThemes;
